import { makeApiCall } from '../core';
import type { ItemEnvelope, ListEnvelope, PaymentPlan } from '@ruta/shared';

// =====================
// Payment Plans API (RUT-2215)
// =====================
//
// These wrappers unwrap the standard response envelopes so callers receive the
// resource directly and never read an envelope key themselves.

export interface PaymentPlanInstallmentInput {
  amount: number;
  dueDate: string; // YYYY-MM-DD
}

export interface CreatePaymentPlanData {
  bundleId: string;
  collectionMode: 'auto_card' | 'manual';
  installments: PaymentPlanInstallmentInput[];
}

export interface UpdatePaymentPlanData {
  collectionMode?: 'auto_card' | 'manual';
  // Full replacement of the schedule when provided.
  installments?: PaymentPlanInstallmentInput[];
}

export const getPaymentPlans = async (
  customerId: string,
): Promise<PaymentPlan[]> => {
  const res = await makeApiCall<ListEnvelope<PaymentPlan>>(
    'GET',
    `/customers/${customerId}/payment-plans`,
  );
  return res.data;
};

export const createPaymentPlan = async (
  customerId: string,
  data: CreatePaymentPlanData,
): Promise<PaymentPlan> => {
  const res = await makeApiCall<ItemEnvelope<PaymentPlan>>(
    'POST',
    `/customers/${customerId}/payment-plans`,
    data,
  );
  return res.data;
};

export const updatePaymentPlan = async (
  customerId: string,
  planId: string,
  data: UpdatePaymentPlanData,
): Promise<PaymentPlan> => {
  const res = await makeApiCall<ItemEnvelope<PaymentPlan>>(
    'PATCH',
    `/customers/${customerId}/payment-plans/${planId}`,
    data,
  );
  return res.data;
};

/** Cancel a payment plan (soft — server marks it cancelled). */
export const cancelPaymentPlan = async (
  customerId: string,
  planId: string,
): Promise<PaymentPlan> => {
  const res = await makeApiCall<ItemEnvelope<PaymentPlan>>(
    'DELETE',
    `/customers/${customerId}/payment-plans/${planId}`,
  );
  return res.data;
};

export interface RecordInstallmentPaymentData {
  amount: number;
  /** Check number or memo for reconciliation. */
  reference?: string;
}

export interface RecordInstallmentPaymentResponse {
  amount: number;
  plan: PaymentPlan;
  completed: boolean;
}

// Record a manual payment against one plan installment, no card charged (RUT-2217).
export const recordInstallmentPayment = async (
  customerId: string,
  planId: string,
  installmentId: string,
  data: RecordInstallmentPaymentData,
): Promise<RecordInstallmentPaymentResponse> => {
  const res = await makeApiCall<ItemEnvelope<RecordInstallmentPaymentResponse>>(
    'POST',
    `/customers/${customerId}/installment-payments`,
    { planId, installmentId, ...data },
  );
  return res.data;
};
